"use client";

import { useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { createClient } from "@/lib/supabase/client";

type AuthMode = "email" | "phone";

export default function LoginPage() {
  const t = useTranslations("auth");
  const tc = useTranslations("common");

  const [mode, setMode] = useState<AuthMode>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supabase = createClient();

  async function handleGoogleLogin() {
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) setError(error.message);
    setLoading(false);
  }

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) setError(error.message);
    else window.location.href = "/scan";
    setLoading(false);
  }

  async function handleSendOtp() {
    setLoading(true);
    setError(null);
    const formattedPhone = phone.startsWith("+91") ? phone : `+91${phone}`;
    const { error } = await supabase.auth.signInWithOtp({
      phone: formattedPhone,
    });
    if (error) setError(error.message);
    else setOtpSent(true);
    setLoading(false);
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const formattedPhone = phone.startsWith("+91") ? phone : `+91${phone}`;
    const { error } = await supabase.auth.verifyOtp({
      phone: formattedPhone,
      token: otp,
      type: "sms",
    });
    if (error) setError(error.message);
    else window.location.href = "/scan";
    setLoading(false);
  }

  return (
    <div className="space-y-6">
      {/* Mobile logo */}
      <div className="lg:hidden text-center">
        <h1 className="text-2xl font-black text-foreground">
          Got<span className="text-brand">RedFlags</span>
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          {tc("tagline")}
        </p>
      </div>

      <div>
        <h2 className="text-xl font-bold text-foreground">{t("loginTitle")}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("loginSubtitle")}
        </p>
      </div>

      {/* Google OAuth */}
      <Button
        variant="outline"
        className="w-full bg-white text-neutral-900 hover:bg-neutral-100 border-neutral-200 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-100 dark:border-neutral-200"
        onClick={handleGoogleLogin}
        disabled={loading}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {t("continueWithGoogle")}
      </Button>

      <div className="relative">
        <Separator className="bg-border" />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
          {tc("or")}
        </span>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 rounded-lg bg-card p-1">
        <button
          className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-colors ${
            mode === "email"
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => {
            setMode("email");
            setError(null);
          }}
        >
          {t("email")}
        </button>
        <button
          className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-colors ${
            mode === "phone"
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => {
            setMode("phone");
            setError(null);
          }}
        >
          {t("phoneOtp")}
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-xs text-destructive">
          {error}
        </div>
      )}

      {mode === "email" ? (
        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground/80 text-sm">
              {t("email")}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={t("emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-card border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-foreground/80 text-sm">
              {t("password")}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder={t("passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-card border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-brand hover:bg-brand/90 text-brand-foreground"
            disabled={loading}
          >
            {loading ? t("signingIn") : t("signIn")}
          </Button>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-foreground/80 text-sm">
              {t("phone")}
            </Label>
            <div className="flex gap-2">
              <div className="flex items-center rounded-md bg-card border border-border px-3 text-sm text-muted-foreground">
                +91
              </div>
              <Input
                id="phone"
                type="tel"
                placeholder={t("phonePlaceholder")}
                value={phone}
                onChange={(e) =>
                  setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                maxLength={10}
                className="bg-card border-border text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {!otpSent ? (
            <Button
              onClick={handleSendOtp}
              className="w-full bg-brand hover:bg-brand/90 text-brand-foreground"
              disabled={loading || phone.length !== 10}
            >
              {loading ? t("sendingOtp") : t("sendOtp")}
            </Button>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-foreground/80 text-sm">
                  {t("enterOtp")}
                </Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder={t("otpPlaceholder")}
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  className="bg-card border-border text-foreground placeholder:text-muted-foreground text-center tracking-widest text-lg"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-brand hover:bg-brand/90 text-brand-foreground"
                disabled={loading || otp.length !== 6}
              >
                {loading ? t("verifying") : t("verifyOtp")}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  setOtp("");
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t("resendOtp")}
              </button>
            </form>
          )}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">
        {t("noAccount")}{" "}
        <Link href="/signup" className="text-brand hover:text-brand/80">
          {t("signUpLink")}
        </Link>
      </p>
    </div>
  );
}
